import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { WaTemplatePicker } from "../components/WaTemplatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Send, Mail, MessageCircle, Save, Trash2, Pencil, FilePlus2, UsersRound, Sparkles, Plus, X,
} from "lucide-react";
import WaSendQueue from "../components/WaSendQueue";
import AiHelper from "../components/AiHelper";

const CHANNELS = [
  { v: "email", l: "Solo Email", icon: Mail },
  { v: "whatsapp", l: "Solo WhatsApp", icon: MessageCircle },
  { v: "both", l: "Email + WhatsApp", icon: Send },
];

const VAR_HELP = "Variabili disponibili nel corpo: {{name}}, {{points}}, {{card}}";

const EMPTY_TPL = { name: "", channel: "both", subject: "", body: "" };

export default function Communications() {
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [tplForm, setTplForm] = useState(EMPTY_TPL);

  // Composer state
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [extraEmailsInput, setExtraEmailsInput] = useState("");
  const [extraEmails, setExtraEmails] = useState([]);
  const [waQueue, setWaQueue] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [waTemplate, setWaTemplate] = useState(null);

  const load = async () => {
    const [t, s] = await Promise.all([
      api.get("/messages/templates"),
      api.get("/segments"),
    ]);
    setTemplates(t.data);
    setSegments(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNewTpl = () => { setTplEditing(null); setTplForm(EMPTY_TPL); setTplOpen(true); };
  const openEditTpl = (t) => {
    setTplEditing(t);
    setTplForm({ name: t.name, channel: t.channel || "both", subject: t.subject || "", body: t.body || "" });
    setTplOpen(true);
  };
  const saveTpl = async () => {
    if (!tplForm.name.trim() || !tplForm.body.trim()) {
      toast.error("Nome e corpo obbligatori"); return;
    }
    try {
      if (tplEditing) await api.patch(`/messages/templates/${tplEditing.id}`, tplForm);
      else await api.post("/messages/templates", tplForm);
      toast.success("Template salvato");
      setTplOpen(false); setTplForm(EMPTY_TPL); setTplEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeTpl = async (id) => {
    if (!window.confirm("Eliminare il template?")) return;
    await api.delete(`/messages/templates/${id}`);
    toast.success("Eliminato"); load();
  };
  const applyTemplate = (t) => {
    setChannel(t.channel || "both");
    setSubject(t.subject || "");
    setBody(t.body || "");
    toast.success(`Template "${t.name}" caricato`);
  };

  const addExtraEmail = () => {
    const email = extraEmailsInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Email non valida"); return;
    }
    if (extraEmails.includes(email)) return;
    setExtraEmails([...extraEmails, email]);
    setExtraEmailsInput("");
  };
  const removeExtraEmail = (email) => {
    setExtraEmails(extraEmails.filter((e) => e !== email));
  };

  const canSend = useMemo(() => {
    if (!body.trim()) return false;
    if ((channel === "email" || channel === "both") && !subject.trim()) return false;
    if (!segmentId && extraEmails.length === 0) return false;
    return true;
  }, [body, subject, channel, segmentId, extraEmails]);

  const send = async () => {
    if (!canSend) return;
    if (!window.confirm(`Confermi l'invio della comunicazione?`)) return;
    setSending(true);
    try {
      const payload = { channel, subject, body };
      if (segmentId) payload.segment_id = segmentId;
      if (extraEmails.length) payload.extra_emails = extraEmails;
      if (waTemplate?.name && (channel === "whatsapp" || channel === "both")) payload.wa_template = waTemplate;
      const { data } = await api.post("/messages/send", payload);
      setLastResult(data);
      toast.success(`Inviate ${data.email_sent} email · ${data.wa_cloud_sent ? `${data.wa_cloud_sent} WhatsApp via Cloud API${data.wa_cloud_failed ? ` (${data.wa_cloud_failed} falliti)` : ""}` : `${data.wa_links.length} link WhatsApp`}`);
      if (data.wa_links.length) {
        setWaQueue({
          messages: data.wa_links.map((l) => ({
            customer_name: l.customer_name, phone: l.phone, wa_link: l.wa_link, message: l.message,
          })),
        });
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const selectedSegment = segments.find((s) => s.id === segmentId);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="communications-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><Send className="h-3 w-3" /> COMUNICAZIONI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Newsletter & broadcast</h1>
          <p className="text-zinc-400 mt-1">Invia messaggi via email o WhatsApp a un cluster di clienti.</p>
        </div>
        <Button data-testid="comm-new-template" onClick={openNewTpl} variant="outline" className="border-zinc-800 h-11">
          <FilePlus2 className="h-4 w-4 mr-2" /> Nuovo template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Templates list */}
        <div className="card-elev p-5 space-y-3" data-testid="comm-templates-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold text-lg">Template salvati</h3>
            <Sparkles className="h-4 w-4 text-amber-300" />
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {templates.length === 0 && (
              <div className="text-sm text-zinc-500 py-4 text-center">
                Nessun template. Creane uno per riutilizzarlo.
              </div>
            )}
            {templates.map((t) => (
              <div key={t.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800" data-testid={`comm-tpl-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {t.channel}
                    </div>
                    {t.subject && <div className="text-xs text-zinc-400 mt-1 truncate">📩 {t.subject}</div>}
                  </div>
                </div>
                <div className="text-xs text-zinc-500 mt-2 line-clamp-2">{t.body}</div>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" data-testid={`comm-use-tpl-${t.id}`} onClick={() => applyTemplate(t)} className="h-7 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] flex-1 text-xs">
                    Usa
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditTpl(t)} className="h-7 border-zinc-800 px-2">
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeTpl(t.id)} className="h-7 border-zinc-800 px-2 hover:border-red-500 hover:text-red-300">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Composer */}
        <div className="card-elev p-5 lg:col-span-2 space-y-4" data-testid="comm-composer">
          <h3 className="font-heading font-semibold text-lg">Componi comunicazione</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {CHANNELS.map((c) => (
              <button
                key={c.v}
                onClick={() => setChannel(c.v)}
                data-testid={`comm-channel-${c.v}`}
                className={`p-3 rounded-lg border flex items-center gap-2 transition
                  ${channel === c.v
                    ? "border-[color:var(--paprika)] bg-red-500/10 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"}`}
              >
                <c.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{c.l}</span>
              </button>
            ))}
          </div>

          {(channel === "whatsapp" || channel === "both") && <WaTemplatePicker value={waTemplate} onChange={setWaTemplate} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                <UsersRound className="h-3 w-3" /> Cluster destinatari
              </Label>
              <Select value={segmentId || "__none__"} onValueChange={(v) => setSegmentId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="comm-segment" className="bg-zinc-950 border-zinc-800">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessun cluster (solo email extra)</SelectItem>
                  {segments.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {s.member_count ?? 0} clienti
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSegment && (
                <div className="text-xs text-zinc-500 mt-1">
                  {selectedSegment.member_count ?? 0} clienti verranno raggiunti
                </div>
              )}
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Email extra (opzionale)</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="comm-extra-email-input"
                  type="email"
                  placeholder="indirizzo@esempio.it"
                  value={extraEmailsInput}
                  onChange={(e) => setExtraEmailsInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtraEmail())}
                  className="bg-zinc-950 border-zinc-800"
                />
                <Button data-testid="comm-extra-email-add" onClick={addExtraEmail} variant="outline" className="border-zinc-800 shrink-0">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {extraEmails.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {extraEmails.map((e) => (
                    <span key={e} className="pill pill-muted text-xs flex items-center gap-1" data-testid={`comm-extra-email-${e}`}>
                      {e}
                      <button onClick={() => removeExtraEmail(e)} className="hover:text-red-400"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(channel === "email" || channel === "both") && (
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Oggetto email *</Label>
              <Input
                data-testid="comm-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Es. Novità dal tuo Kebab Club"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-300 inline-block">Corpo messaggio *</Label>
              <AiHelper
                preset={channel === "whatsapp" ? "whatsapp_template" : "broadcast_copy"}
                contextLabel={channel === "whatsapp" ? "Suggerisci testo WhatsApp" : "Suggerisci testo broadcast"}
                hint="Es. Riattivazione clienti dormienti da 60 giorni, tono caldo, sconto 15%"
                triggerLabel="AI Suggerisci"
                testid="comm-ai-suggest"
                onApply={(v) => {
                  if (typeof v === "object" && v) {
                    if (v.subject && !subject) setSubject(v.subject);
                    if (v.body_text || v.body_html) {
                      setBody(v.body_text || v.body_html.replace(/<[^>]+>/g, ""));
                    } else if (v.body) {
                      setBody(v.body);
                      if (v.title && !subject) setSubject(v.title);
                    }
                  } else if (typeof v === "string") {
                    setBody(v);
                  }
                }}
              />
            </div>
            <textarea
              data-testid="comm-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Ciao {{name}}, questa settimana torna il nostro pollo croccante 🌶️"
              className="w-full rounded-md p-3 bg-zinc-950 border border-zinc-800 text-sm text-zinc-100"
            />
            <div className="text-[11px] text-zinc-500 mt-1">{VAR_HELP}</div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 justify-between border-t border-zinc-900 pt-4">
            <div className="text-xs text-zinc-500">
              {segmentId ? "Includerà tutti i clienti del cluster (con canale disponibile)." : "Solo email extra."}
              {" "}Le email sono inviate via Resend gestito da Emergent.
            </div>
            <Button
              data-testid="comm-send-button"
              disabled={!canSend || sending}
              onClick={send}
              className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11 px-6"
            >
              <Send className="h-4 w-4 mr-2" /> {sending ? "Invio in corso…" : "Invia comunicazione"}
            </Button>
          </div>

          {lastResult && (
            <div className="mt-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs space-y-1" data-testid="comm-result">
              <div className="font-semibold text-emerald-300">Invio completato</div>
              <div>📧 Email inviate: <strong>{lastResult.email_sent}</strong></div>
              <div>💬 Link WhatsApp generati: <strong>{lastResult.wa_links.length}</strong></div>
              <div>👥 Clienti nel cluster: <strong>{lastResult.members_targeted}</strong></div>
              {lastResult.email_errors.length > 0 && (
                <div className="text-red-300">
                  Errori email: {lastResult.email_errors.length}
                  <ul className="ml-4 mt-1 list-disc">
                    {lastResult.email_errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e.email}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {lastResult.wa_links.length > 3 && (
                <div className="text-zinc-400 mt-1">
                  Prime 3 chat WhatsApp aperte automaticamente. Le altre sono nel log.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{tplEditing ? "Modifica template" : "Nuovo template"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Nome template *</Label>
              <Input data-testid="tpl-form-name" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Canale</Label>
              <Select value={tplForm.channel} onValueChange={(v) => setTplForm({ ...tplForm, channel: v })}>
                <SelectTrigger data-testid="tpl-form-channel" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Oggetto (email)</Label>
              <Input data-testid="tpl-form-subject" value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Corpo *</Label>
              <textarea
                data-testid="tpl-form-body"
                value={tplForm.body}
                onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })}
                rows={6}
                className="w-full rounded-md p-2 bg-zinc-950 border border-zinc-800 text-sm text-zinc-100"
              />
              <div className="text-[11px] text-zinc-500 mt-1">{VAR_HELP}</div>
            </div>
            <Button data-testid="tpl-form-submit" onClick={saveTpl} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              <Save className="h-4 w-4 mr-2" /> {tplEditing ? "Salva" : "Crea template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <WaSendQueue open={!!waQueue} onClose={() => setWaQueue(null)} messages={waQueue?.messages || []} />
    </div>
  );
}
